
import { useCallback, useState, useEffect } from "react";
import { Canvas } from "fabric";
import { CustomCanvas } from "@/types/fabric";
import { Tool } from "./use-canvas-setup";
import { toast } from "@/hooks/use-toast";
import { ScenarioType } from "@/types/analysis";
import { useUndoRedo } from "./use-undo-redo";

interface UseCanvasEventsProps {
  canvas: Canvas | null;
  activeTool: Tool;
  onCurrentDrawingIdChange?: (id: string | null) => void;
  onResetAnalysis?: () => void;
}

export const useCanvasEvents = ({ 
  canvas, 
  activeTool, 
  onCurrentDrawingIdChange,
  onResetAnalysis 
}: UseCanvasEventsProps) => {
  const [hasSelection, setHasSelection] = useState(false);
  const { undo, redo, canUndo, canRedo, clearHistory } = useUndoRedo(canvas);

  useEffect(() => {
    if (!canvas) return;

    const updateSelectionState = () => {
      const activeObjects = canvas.getActiveObjects();
      setHasSelection(activeObjects.length > 0);
    };

    canvas.on('selection:created', updateSelectionState);
    canvas.on('selection:updated', updateSelectionState);
    canvas.on('selection:cleared', updateSelectionState);

    updateSelectionState();

    return () => {
      canvas.off('selection:created', updateSelectionState);
      canvas.off('selection:updated', updateSelectionState);
      canvas.off('selection:cleared', updateSelectionState);
    };
  }, [canvas]);

  useEffect(() => {
    if (!canvas) return;
    
    const handlePathCreated = (e: any) => {
      // Removed eraser-specific conditional check
      if (e.path) {
        console.log('Path created, updating canvas state for undo/redo');
        if ((canvas as CustomCanvas).data) {
          (canvas as CustomCanvas).data.isModified = true;
          (canvas as CustomCanvas).data.lastModified = new Date().toISOString();
        }
      }
    };
    
    canvas.on('path:created', handlePathCreated);
    
    return () => {
      canvas.off('path:created', handlePathCreated);
    };
  }, [canvas, activeTool]);

  const handleLoadDrawing = useCallback(async (drawingData: string, drawingId: string, analysisData?: any) => {
    if (!canvas) return;
    
    try {
      const currentData = (canvas as CustomCanvas).data;
      if (currentData?.isModified) {
        toast({
          title: "Loading new drawing",
          description: "Loading a new drawing will discard unsaved changes.",
        });
      }
      
      console.log('Loading drawing. Current canvas ID:', currentData?.currentDrawingId, 'New ID:', drawingId);
      console.log('Analysis data from history:', analysisData);
      
      if (onResetAnalysis) {
        console.log('Resetting analysis before loading drawing');
        onResetAnalysis();
      }
      
      // Clear the canvas and prepare for new data
      canvas.clear();
      (canvas as CustomCanvas).data = null;
      
      const parsedDrawing = JSON.parse(drawingData);
      if (!parsedDrawing || !Array.isArray(parsedDrawing.objects)) {
        throw new Error('Invalid drawing data structure');
      }
      
      console.log('Parsed drawing objects count:', parsedDrawing.objects.length);
      
      // Set canvas data including analysis data
      (canvas as CustomCanvas).data = {
        currentDrawingId: drawingId,
        lastLoaded: new Date().toISOString(),
        lastModified: new Date().toISOString(),
        version: parsedDrawing.version || 1,
        isModified: false,
        // Important: Set the analysis data from the loaded drawing
        analysisText: analysisData?.analysis_text || null,
        scenarioType: (analysisData?.procedure_type as ScenarioType) || null,
        procedureType: analysisData?.procedure_type || null
      };

      console.log('Canvas data after setting:', (canvas as CustomCanvas).data);

      if (onCurrentDrawingIdChange) {
        onCurrentDrawingIdChange(drawingId);
      }
      
      // Load the drawing JSON data into the canvas
      canvas.loadFromJSON(parsedDrawing, () => {
        setTimeout(() => {
          const loadedObjectCount = canvas.getObjects().length;
          
          if (loadedObjectCount > 0) {
            canvas.getObjects().forEach(obj => {
              obj.selectable = true;
              obj.evented = true;
              obj.visible = true;
            });

            canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
            canvas.requestRenderAll();
            
            console.log('Drawing loaded successfully. Canvas data:', (canvas as CustomCanvas).data);
            
            // Show different toast based on whether analysis exists
            if (analysisData?.analysis_text) {
              toast({
                title: "Drawing loaded with analysis",
                description: "AI analysis is available for this drawing."
              });
            } else {
              toast({
                title: "Drawing loaded"
              });
            }
            
            clearHistory();
          } else {
            console.warn('No objects loaded in the canvas');
            toast({
              variant: "destructive",
              title: "Error",
              description: "Could not load the drawing.",
            });
          }
        }, 100);
      });

      // Set up event listeners for tracking modifications
      canvas.on('object:added', () => {
        if ((canvas as CustomCanvas).data) {
          (canvas as CustomCanvas).data.isModified = true;
          (canvas as CustomCanvas).data.lastModified = new Date().toISOString();
        }
      });

      canvas.on('object:modified', () => {
        if ((canvas as CustomCanvas).data) {
          (canvas as CustomCanvas).data.isModified = true;
          (canvas as CustomCanvas).data.lastModified = new Date().toISOString();
        }
      });

      canvas.on('object:removed', () => {
        if ((canvas as CustomCanvas).data) {
          (canvas as CustomCanvas).data.isModified = true;
          (canvas as CustomCanvas).data.lastModified = new Date().toISOString();
        }
      });

    } catch (error) {
      console.error('Error loading drawing:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Could not load the drawing.",
      });
      (canvas as CustomCanvas).data = {
        currentDrawingId: null,
        lastModified: new Date().toISOString(),
        lastLoaded: null,
        version: 1,
        isModified: false,
        analysisText: null,
        scenarioType: null,
        procedureType: null
      };
      if (onCurrentDrawingIdChange) {
        onCurrentDrawingIdChange(null);
      }
    }
  }, [canvas, onCurrentDrawingIdChange, clearHistory, onResetAnalysis]);

  const undoLastAction = useCallback(() => {
    if (!canvas) return;
    
    const result = undo();
    if (result) {
      toast({
        title: "Undo successful",
        description: "Last action was undone.",
      });
    } else {
      toast({
        description: "Nothing to undo",
      });
    }
  }, [canvas, undo]);

  const redoLastAction = useCallback(() => {
    if (!canvas) return;
    
    const result = redo();
    if (result) {
      toast({
        title: "Redo successful",
        description: "Action was redone.",
      });
    } else {
      toast({
        description: "Nothing to redo",
      });
    }
  }, [canvas, redo]);

  const deleteSelectedObjects = useCallback(() => {
    if (!canvas) return;
    
    const activeObjects = canvas.getActiveObjects();
    if (activeObjects.length === 0) {
      console.log('No objects selected for deletion');
      return;
    }
    
    console.log(`Deleting ${activeObjects.length} selected objects`);
    activeObjects.forEach((obj) => {
      canvas.remove(obj);
    });
    canvas.discardActiveObject();
    canvas.renderAll();
    
    if ((canvas as CustomCanvas).data) {
      (canvas as CustomCanvas).data.isModified = true;
      (canvas as CustomCanvas).data.lastModified = new Date().toISOString();
    }
  }, [canvas]);

  return {
    hasSelection,
    handleLoadDrawing,
    undoLastAction,
    redoLastAction,
    deleteSelectedObjects,
    canUndo,
    canRedo
  };
};
